import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAuth } from 'src/boot/auth';
import { useSchoolFiltersStore } from 'src/stores/schoolFilters';
import {
  useSchoolData,
  type EnrollmentStatus,
  type StudentProfileEnrollment,
} from './useSchoolData';

/**
 * One class the signed-in student is enrolled in, flattened out of the
 * enrollment row so components never dig through optional relations.
 */
export interface StudentClass {
  enrollmentDocumentId: string;
  classDocumentId: string;
  className: string;
  classCode: string;
  schoolName: string;
  schoolDocumentId: string;
  semesterName: string;
  semesterDocumentId: string;
  status: EnrollmentStatus | '';
}

// Module-scoped so the drawer selector and the dashboard card list read the
// same fetch instead of each hitting /school-enrollments on their own.
const enrollments = ref<StudentClass[]>([]);
const loading = ref(false);
const loaded = ref(false);
/** Serializes the last requested context, so a stale response can't overwrite a newer one. */
let requestToken = 0;

/** Exported for the dashboard's semester overview, which reads the same rows. */
export function toStudentClass(row: StudentProfileEnrollment): StudentClass {
  const schoolClass = row.school_class;
  const school = schoolClass?.school;
  return {
    enrollmentDocumentId: row.documentId,
    classDocumentId: schoolClass?.documentId || '',
    className: schoolClass?.name || '',
    classCode: schoolClass?.code || '',
    schoolName: school?.short_name || school?.full_name || '',
    schoolDocumentId: school?.documentId || '',
    semesterName: row.semester?.name || '',
    semesterDocumentId: row.semester?.documentId || '',
    status: row.status || row.enrollment_status || '',
  };
}

export function useStudentClasses() {
  const { t } = useI18n();
  const { currentUser } = useAuth();
  const { fetchStudentEnrollments } = useSchoolData();
  const schoolFiltersStore = useSchoolFiltersStore();

  /**
   * Re-reads the student's enrollments for the given school/semester context.
   * Both ids are optional: "All schools" and an unseeded semester simply widen
   * the scope. The backend scopes the result to the caller regardless.
   */
  async function fetchStudentClasses(params: {
    schoolDocumentId?: string;
    semesterDocumentId?: string;
  }) {
    const studentDocumentId = currentUser.value?.documentId || '';
    if (!studentDocumentId) {
      enrollments.value = [];
      loaded.value = true;
      return;
    }

    const token = ++requestToken;
    loading.value = true;
    try {
      const rows = await fetchStudentEnrollments(
        studentDocumentId,
        params.semesterDocumentId || undefined,
        params.schoolDocumentId || undefined,
      );
      if (token !== requestToken) return;
      enrollments.value = rows
        .map(toStudentClass)
        .filter((row) => !!row.classDocumentId);
      loaded.value = true;
    } finally {
      if (token === requestToken) loading.value = false;
    }
  }

  /** Enrollments the student has, in the currently selected school/semester. */
  const classes = computed(() => enrollments.value);

  /**
   * The class dropdown always offers "All classes" (empty value) first, so the
   * default selection is the unfiltered one.
   */
  const classOptions = computed(() => [
    { label: t('SCHOOL_ALL_CLASSES'), value: '' },
    ...enrollments.value.map((row) => ({
      label: row.classCode ? `${row.className} (${row.classCode})` : row.className,
      value: row.classDocumentId,
    })),
  ]);

  /**
   * A single class makes the selector pure noise — "All classes" and that class
   * resolve to the same list — so it is hidden and the default stays active.
   */
  const showClassSelector = computed(() => enrollments.value.length > 1);

  const selectedClass = computed<string>({
    get: () => schoolFiltersStore.selectedClassDocumentId,
    set: (value: string) =>
      schoolFiltersStore.setSelectedClassDocumentId(value || ''),
  });

  /** Drops a selection that the current school/semester scope no longer contains. */
  function reconcileSelectedClass() {
    const selected = schoolFiltersStore.selectedClassDocumentId;
    if (!selected) return;
    if (enrollments.value.some((row) => row.classDocumentId === selected)) {
      return;
    }
    schoolFiltersStore.setSelectedClassDocumentId('');
  }

  return {
    classes,
    classOptions,
    showClassSelector,
    selectedClass,
    loading,
    loaded,
    fetchStudentClasses,
    reconcileSelectedClass,
  };
}
